import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Calendar, HeartHandshake, Building2, MessageCircle, Phone, BookCopy } from 'lucide-react';
import { configService } from '@/services/config';
import { toast } from 'sonner';
import { LoadingDialog } from '@/components/ui/loading-dialog';

interface ContentSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface MissionHubConfig {
  sections: {
    [key: string]: {
      enabled: boolean;
      updatedAt: string;
    };
  };
}

const contentSections: ContentSection[] = [
  {
    id: 'community-events',
    title: 'Community Events',
    description: 'Show upcoming events and volunteer opportunities in your community.',
    icon: <Calendar className="h-5 w-5" />,
  },
  {
    id: 'campaigns',
    title: 'Campaigns',
    description: 'Display your ongoing fundraising and awareness campaigns.',
    icon: <HeartHandshake className="h-5 w-5" />,
  },
  {
    id: 'corporate-opportunities',
    title: 'Corporate Opportunities',
    description: 'Showcase partnership and sponsorship opportunities for businesses.',
    icon: <Building2 className="h-5 w-5" />,
  },
  {
    id: 'contact-us',
    title: 'Contact Us',
    description: 'Enable the contact form for inquiries and support.',
    icon: <Phone className="h-5 w-5" />,
  },
  {
    id: 'chat',
    title: 'Chat',
    description: 'Allow visitors to chat with your organization in real-time.',
    icon: <MessageCircle className="h-5 w-5" />,
  },
];

const MissionHubContentPage = ({ profile, session }: { profile: any | null, session: any | null }) => {
  const [enabledSections, setEnabledSections] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [processingSection, setProcessingSection] = useState<string | null>(null);

  useEffect(() => {
    if (session?.access_token) {
      configService.updateToken(session.access_token);
      loadConfigurations();
    }
  }, [session?.access_token]);

  const loadConfigurations = async () => {
    try {
      const response = await configService.getConfig('npo.missionhub');
      if (response.success && response.data) {
        const config = response.data as MissionHubConfig;
        const enabledSet = new Set<string>();
        
        Object.entries(config.sections || {}).forEach(([sectionId, section]) => {
          if (section.enabled) {
            enabledSet.add(sectionId);
          }
        });
        
        setEnabledSections(enabledSet);
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      toast.error('Failed to load section configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = async (sectionId: string) => {
    const isEnabled = enabledSections.has(sectionId);
    setProcessingSection(sectionId);
    
    try {
      // Get current config first
      const currentConfig = await configService.getConfig('npo.missionhub');
      const config: MissionHubConfig = currentConfig.success ? (currentConfig.data || { sections: {} }) : { sections: {} };
      
      // Update the specific section
      config.sections = {
        ...config.sections,
        [sectionId]: {
          enabled: !isEnabled,
          updatedAt: new Date().toISOString()
        }
      };

      const response = await configService.setConfig('npo.missionhub', {
        value: config,
        description: 'Mission Hub section configurations',
        isPublic: true
      });

      if (response) {
        const newEnabledSections = new Set(enabledSections);
        if (isEnabled) {
          newEnabledSections.delete(sectionId);
        } else {
          newEnabledSections.add(sectionId);
        }
        setEnabledSections(newEnabledSections);
        toast.success(`${sectionId} section ${isEnabled ? 'disabled' : 'enabled'} successfully`);
      } else {
        toast.error(`Failed to ${isEnabled ? 'disable' : 'enable'} ${sectionId} section`);
      }
    } catch (error) {
      console.error(`Error toggling ${sectionId}:`, error);
      toast.error(`Failed to update ${sectionId} section`);
    } finally {
      setProcessingSection(null);
    }
  };

  return (
    <div className="space-y-6">
      <LoadingDialog 
        open={processingSection !== null} 
        title="Updating Section"
        description={`Please wait while we ${enabledSections.has(processingSection || '') ? 'disable' : 'enable'} the section...`}
      />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookCopy className="h-6 w-6" />
            Content Management
          </CardTitle>
          <CardDescription>
            Customize which sections are visible on your mission hub page.
            Toggle sections on/off to control what visitors can see and interact with.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {contentSections.map((section) => (
              <div
                key={section.id}
                className="flex items-start justify-between space-x-4 rounded-lg border p-4"
              >
                <div className="flex items-start space-x-4">
                  <div className="mt-0.5 bg-gray-100 p-2 rounded-md">
                    {section.icon}
                  </div>
                  <div className="space-y-1">
                    <Label
                      htmlFor={section.id}
                      className="text-base font-semibold"
                    >
                      {section.title}
                    </Label>
                    <p className="text-sm text-gray-500">
                      {section.description}
                    </p>
                  </div>
                </div>
                <Switch
                  id={section.id}
                  checked={enabledSections.has(section.id)}
                  onCheckedChange={() => toggleSection(section.id)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MissionHubContentPage;
